import { useCallback, useEffect, useRef, useState } from "react";
import { Eye, FileCode2, Mail, Send, Users } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type Recipient = {
  name?: string;
  email: string;
};

type RecipientInsight = {
  id: string;
  email: string;
  name: string | null;
  status: string;
  firstSentAt: string | null;
  lastSentAt: string | null;
  lastOpenedAt: string | null;
  lastClickedAt: string | null;
  lastClickedUrl: string | null;
  lastRegisteredAt: string | null;
  lastRegistrationId: string | null;
  lastRegistrationOrgNumber: string | null;
  totalSentCount: number;
  totalOpenCount: number;
  totalClickCount: number;
  totalRegistrationCount: number;
  score: number;
};

type SendInsight = {
  id: string;
  recipientId: string;
  email: string;
  name: string | null;
  subject: string;
  status: string;
  openCount: number;
  clickCount: number;
  queuedAt: string;
  sentAt: string | null;
  lastOpenedAt: string | null;
  lastClickedAt: string | null;
  lastClickedUrl: string | null;
  convertedAt: string | null;
  registrationId: string | null;
  registrationOrgNumber: string | null;
};

type MarketingInsights = {
  summary: {
    totalRecipients: number;
    totalEmailsSent: number;
    openedRecipients: number;
    clickedRecipients: number;
    convertedRecipients: number;
  };
  recipients: RecipientInsight[];
  recentSends: SendInsight[];
};

const STORAGE_KEY = "marketing-draft";

function loadDraft() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as { subject?: string; recipientInput?: string; html?: string }) : null;
  } catch {
    return null;
  }
}

const defaultPreviewText = "Hei {{firstName}}, dette er en forhåndsvisning av meldingen din.";

const defaultHtml = `<h1>Ny auksjonsplattform for bilforhandlere</h1>

<p>Hei {{name}},</p>

<p>
<strong>1. april lanseres Forhandlerbørsen</strong> – en ny auksjonsplattform laget spesielt for bilforhandlere som ønsker en raskere og enklere måte å håndtere innbyttebiler på.
</p>

<p>
Plattformen gjør det mulig å selge eller prise biler direkte mot andre forhandlere – ofte før bilen i det hele tatt er kommet inn på lager.
</p>

<h2>Gratis å bruke</h2>

<p>
Det er <strong>helt gratis å selge biler</strong> på Forhandlerbørsen.
</p>

<p>
✔ Gratis å opprette bruker<br>
✔ Gratis å liste biler på auksjon<br>
✔ Ingen oppstartsavgift
</p>

<h2>Perfekt for trade-in biler</h2>

<p>
Har du en innbyttebil du ikke ønsker å beholde selv?
</p>

<p>
Med Forhandlerbørsen kan du:
</p>

<p>
• Få prisindikasjon fra markedet<br>
• Selge bilen videre til andre forhandlere<br>
• Redusere lager og kapitalbinding
</p>

<h2>15 minutters lynauksjon</h2>

<p>
Vi introduserer <strong>lynauksjon på kun 15 minutter</strong>.
</p>

<p>
Dette gjør det mulig å:
</p>

<p>
• prise innbyttebiler raskt<br>
• selge biler før de kommer inn<br>
• ta bedre beslutninger i salgsprosessen
</p>

<p>
Målet er enkelt: <strong>gjøre håndtering av innbyttebiler raskere, tryggere og mer effektivt.</strong>
</p>

<br>

<a href="https://www.forhandlerborsen.no/registrer" class="button">
Registrer deg før lansering
</a>`;

const emailLayout = (content: string, previewText?: string) => `
<!DOCTYPE html>
<html lang="nb">
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Forhandlerbørsen</title>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Outfit:wght@700;800;900&display=swap" rel="stylesheet">
    <style>
        body { margin: 0; padding: 0; background-color: #F7F3EB; font-family: 'Inter', system-ui, sans-serif; -webkit-font-smoothing: antialiased; }
        .wrapper { width: 100%; table-layout: fixed; background-color: #F7F3EB; padding-bottom: 40px; }
        .main { background-color: #ffffff; margin: 0 auto; width: 100%; max-width: 600px; border-spacing: 0; color: #001F1E; }
        .header { background-color: #001F1E; padding: 40px 0; text-align: center; }
        .content { padding: 40px 30px; line-height: 1.6; }
        .footer { padding: 30px; text-align: center; color: #64748B; font-size: 13px; }
        .button { background-color: #FF5A1F; color: #ffffff !important; padding: 16px 32px; border-radius: 50px; text-decoration: none; font-weight: 700; display: inline-block; box-shadow: 0 10px 20px rgba(255, 90, 31, 0.2); }
        h1, h2 { font-family: 'Outfit', sans-serif; color: #001F1E; margin-top: 0; letter-spacing: -0.02em; }
        p { margin-bottom: 20px; font-size: 16px; font-weight: 500; }
        .preview { display: none; font-size: 0; color: transparent; line-height: 1px; max-height: 0px; max-width: 0px; opacity: 0; overflow: hidden; }
    </style>
</head>
<body>
    ${previewText ? `<div class="preview">${previewText}</div>` : ""}
    <div class="wrapper">
        <center>
            <table class="main" role="presentation">
                <tr>
                    <td class="header">
                        <img src="https://forhandlerborsen.no/branding/logo-dark.svg" alt="Forhandlerbørsen" width="200" style="display: block; margin: 0 auto;">
                    </td>
                </tr>
                <tr>
                    <td class="content">
                        ${content}
                    </td>
                </tr>
                <tr>
                    <td class="footer">
                        <strong>Forhandlerbørsen</strong><br>
                        Norges mest effektive B2B markedsplass for bilhandel<br><br>
                        &copy; ${new Date().getFullYear()} Forhandlerbørsen. Alle rettigheter reservert.
                    </td>
                </tr>
            </table>
        </center>
    </div>
</body>
</html>
`;

function parseRecipients(input: string) {
  const lines = input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const recipients: Recipient[] = [];
  const invalidRows: string[] = [];
  const seen = new Set<string>();

  for (const line of lines) {
    const parts = line
      .split(/\t|;|,/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length === 0) {
      invalidRows.push(line);
      continue;
    }

    const [firstPart, secondPart] = parts;
    const emailCandidate = parts.length === 1 ? firstPart : secondPart;
    const name = parts.length === 1 ? undefined : firstPart;
    const email = emailCandidate.toLowerCase();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!emailValid) {
      invalidRows.push(line);
      continue;
    }

    if (seen.has(email)) {
      continue;
    }

    seen.add(email);
    recipients.push(name ? { name, email } : { email });
  }

  return { recipients, invalidRows };
}

function renderMarketingTemplate(html: string, recipient: Recipient | null) {
  if (!recipient) {
    return html;
  }

  const trimmedName = recipient.name?.trim();
  const [firstName = "der"] = trimmedName ? trimmedName.split(/\s+/) : [];
  const fullName = trimmedName || "der";

  return html
    .replaceAll("{{name}}", fullName)
    .replaceAll("{{firstName}}", firstName)
    .replaceAll("{{email}}", recipient.email);
}

function formatDateTime(value: string | null) {
  if (!value) return "Ikke registrert";

  return new Intl.DateTimeFormat("nb-NO", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function MarketingPage() {
  const draft = useRef(loadDraft());
  const [subject, setSubject] = useState(draft.current?.subject ?? "Invitasjon fra Forhandlerbørsen");
  const [recipientInput, setRecipientInput] = useState(draft.current?.recipientInput ?? "");
  const [html, setHtml] = useState(draft.current?.html ?? defaultHtml);
  const [activeTab, setActiveTab] = useState("html");
  const [isSending, setIsSending] = useState(false);
  const [sendMessage, setSendMessage] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [insights, setInsights] = useState<MarketingInsights | null>(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(true);

  const saveDraft = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ subject, recipientInput, html }));
  }, [subject, recipientInput, html]);

  useEffect(() => {
    saveDraft();
  }, [saveDraft]);

  const { recipients, invalidRows } = parseRecipients(recipientInput);
  const previewRecipient = recipients[0] ?? null;
  const previewHtml = emailLayout(
    renderMarketingTemplate(html, previewRecipient),
    renderMarketingTemplate(defaultPreviewText, previewRecipient),
  );
  const recipientInsightMap = new Map(insights?.recipients.map((recipient) => [recipient.email, recipient]) ?? []);
  const knownRecipients = recipients
    .map((recipient) => ({
      input: recipient,
      insight: recipientInsightMap.get(recipient.email),
    }))
    .filter((entry): entry is { input: Recipient; insight: RecipientInsight } => Boolean(entry.insight));

  const loadInsights = async () => {
    setIsLoadingInsights(true);
    try {
      const response = await fetch(`${API}/api/admin/marketing/insights`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Kunne ikke hente marketing-innsikt.");
      }

      const payload = (await response.json()) as MarketingInsights;
      setInsights(payload);
    } catch {
      setInsights(null);
    } finally {
      setIsLoadingInsights(false);
    }
  };

  useEffect(() => {
    void loadInsights();
  }, []);

  const handleSend = async () => {
    setSendMessage(null);
    setSendError(null);

    if (!subject.trim()) {
      setSendError("Legg inn et emne før utsending.");
      return;
    }

    if (!html.trim()) {
      setSendError("Legg inn HTML før utsending.");
      return;
    }

    if (!recipients.length) {
      setSendError("Legg inn minst én gyldig mottaker.");
      return;
    }

    setIsSending(true);
    try {
      const response = await fetch(`${API}/api/admin/marketing/send-email`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subject: subject.trim(),
          html,
          recipients,
        }),
      });

      const payload = (await response.json().catch(() => null)) as
        | { queued?: number; failed?: number; error?: string }
        | null;

      if (!response.ok) {
        throw new Error(payload?.error || "Kunne ikke sende e-postene.");
      }

      setSendMessage(
        payload?.failed
          ? `La ${payload?.queued ?? recipients.length} e-poster i kø. ${payload.failed} ble ikke lagt i kø.`
          : `La ${payload?.queued ?? recipients.length} e-poster i kø for utsending.`,
      );
      setRecipientInput("");
      await loadInsights();
    } catch (error) {
      setSendError(error instanceof Error ? error.message : "Ukjent feil ved utsending.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-screen-xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
      <div className="rounded-[28px] border border-border bg-[linear-gradient(135deg,#fffaf1_0%,#f4efe5_100%)] p-6 shadow-sm sm:p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <Badge variant="accent">Marketing</Badge>
            <div>
              <h1
                className="text-3xl font-black tracking-tight text-foreground"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Bulk e-post
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                Lim inn e-postadresser direkte fra Excel, eller bruk navn + e-post hvis du vil
                personalisere meldingen. Systemet sender én e-post per mottaker.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-black/5 bg-white/70 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Mottakere</p>
              <p className="mt-1 text-2xl font-black tracking-tight text-foreground">{recipients.length}</p>
            </div>
            <div className="rounded-2xl border border-black/5 bg-white/70 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Ugyldige rader</p>
              <p className="mt-1 text-2xl font-black tracking-tight text-foreground">{invalidRows.length}</p>
            </div>
            <div className="rounded-2xl border border-black/5 bg-white/70 px-4 py-3 col-span-2 sm:col-span-1">
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Kjente mottakere</p>
              <p className="mt-1 truncate text-2xl font-black tracking-tight text-foreground">
                {knownRecipients.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[0.95fr_1.25fr]">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">Mottakerliste</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Lim inn fra Excel som `E-post`, `Navn[TAB]E-post` eller `Navn, E-post`
                </p>
              </div>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <textarea
              value={recipientInput}
              onChange={(event) => setRecipientInput(event.target.value)}
              placeholder={`ola@example.no\nkari@example.no\nOla Nordmann\tola@example.no`}
              className="min-h-72 w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm leading-6 outline-none transition focus-visible:ring-2 focus-visible:ring-ring"
            />

            <div className="rounded-2xl border border-border bg-muted/35 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-foreground">Gyldige mottakere</p>
                <Badge variant="muted">{recipients.length}</Badge>
              </div>
              <div className="mt-3 max-h-56 space-y-2 overflow-y-auto">
                {recipients.length ? (
                  recipients.map((recipient) => (
                    <div
                      key={recipient.email}
                      className="flex items-center justify-between gap-3 rounded-xl bg-background px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">{recipient.name || recipient.email}</p>
                        {recipient.name ? <p className="truncate text-xs text-muted-foreground">{recipient.email}</p> : null}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">Ingen mottakere tolket ennå.</p>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-muted/35 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-foreground">Historikk på innlimte adresser</p>
                <Badge variant="muted">{knownRecipients.length}</Badge>
              </div>
              <div className="mt-3 max-h-56 space-y-2 overflow-y-auto">
                {knownRecipients.length ? (
                  knownRecipients.map(({ input, insight }) => (
                    <div key={input.email} className="rounded-xl bg-background px-3 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground">
                            {input.name || insight.name || input.email}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">{input.email}</p>
                        </div>
                        <Badge variant={insight.totalRegistrationCount > 0 ? "accent" : "muted"}>
                          Score {insight.score}
                        </Badge>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        Sendt {insight.totalSentCount} ganger, åpnet {insight.totalOpenCount}, klikket {insight.totalClickCount}, registrert {insight.totalRegistrationCount}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {isLoadingInsights ? "Laster historikk..." : "Ingen av de innlimte adressene finnes i historikken ennå."}
                  </p>
                )}
              </div>
            </div>

            {!!invalidRows.length && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-semibold text-amber-800">Rader som ikke kunne tolkes</p>
                <div className="mt-2 max-h-32 space-y-1 overflow-y-auto text-xs text-amber-700">
                  {invalidRows.map((row) => (
                    <p key={row}>{row}</p>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">E-postbygger</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Rediger innholdet i e-posten direkte og se preview for første mottaker i listen
                </p>
              </div>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </div>

            <div className="grid gap-3">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Emne
                </label>
                <Input
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  placeholder="Skriv e-postemne"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>Merge tags:</span>
                <Badge variant="muted">{"{{name}}"}</Badge>
                <Badge variant="muted">{"{{firstName}}"}</Badge>
                <Badge variant="muted">{"{{email}}"}</Badge>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="html" className="gap-2">
                  <FileCode2 className="h-4 w-4" />
                  HTML
                </TabsTrigger>
                <TabsTrigger value="preview" className="gap-2">
                  <Eye className="h-4 w-4" />
                  Preview
                </TabsTrigger>
              </TabsList>

              <TabsContent value="html">
                <textarea
                  value={html}
                  onChange={(event) => setHtml(event.target.value)}
                  className="w-full resize-y rounded-2xl border border-input bg-[#fffdf8] px-4 py-3 font-mono text-[13px] leading-6 outline-none transition focus-visible:ring-2 focus-visible:ring-ring"
                  style={{ minHeight: 920, height: 920 }}
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  Denne editoren gjelder kun innholdet i meldingen. Logo, ramme og footer legges på automatisk i e-posttemplate-en.
                </p>
              </TabsContent>

              <TabsContent value="preview">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-muted/35 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{subject || "Uten emne"}</p>
                      <p className="text-xs text-muted-foreground">
                        Preview for {previewRecipient
                          ? previewRecipient.name
                            ? `${previewRecipient.name} <${previewRecipient.email}>`
                            : previewRecipient.email
                          : "første mottaker når listen er fylt ut"}
                      </p>
                    </div>
                    <Badge variant={previewRecipient ? "accent" : "muted"}>
                      {previewRecipient ? "Personalisert" : "Standard"}
                    </Badge>
                  </div>

                  <div className="overflow-hidden rounded-[24px] border border-border bg-white">
                    <iframe
                      title="Marketing email preview"
                      srcDoc={previewHtml}
                      className="w-full bg-white"
                      style={{ minHeight: 920, height: 920 }}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <div className="mt-5 flex flex-col gap-3 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                {sendMessage ? <p className="text-sm font-medium text-emerald-700">{sendMessage}</p> : null}
                {sendError ? <p className="text-sm font-medium text-destructive">{sendError}</p> : null}
                <p className="text-xs text-muted-foreground">
                  Utsending legger én e-post per mottaker i kø. Mottakerne ser ikke hverandre.
                </p>
              </div>

              <Button onClick={() => void handleSend()} disabled={isSending || !recipients.length} className="gap-2">
                <Send className="h-4 w-4" />
                {isSending ? "Sender..." : `Send til ${recipients.length || 0}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">Engasjement</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Samlet oversikt over utsendinger, åpninger, klikk og registreringer.
                </p>
              </div>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["Lagrede mottakere", insights?.summary.totalRecipients ?? 0],
                ["Sendte e-poster", insights?.summary.totalEmailsSent ?? 0],
                ["Har åpnet", insights?.summary.openedRecipients ?? 0],
                ["Har klikket", insights?.summary.clickedRecipients ?? 0],
                ["Har registrert seg", insights?.summary.convertedRecipients ?? 0],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-border bg-muted/35 px-4 py-3">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
                  <p className="mt-1 text-2xl font-black tracking-tight text-foreground">
                    {isLoadingInsights ? "..." : value}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">Siste aktivitet</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  De nyeste utsendingene med registrerte åpninger, klikk og konverteringer.
                </p>
              </div>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-[28rem] space-y-3 overflow-y-auto">
              {insights?.recentSends.length ? (
                insights.recentSends.map((send) => (
                  <div key={send.id} className="rounded-2xl border border-border bg-muted/20 px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-foreground">
                          {send.name || send.email}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">{send.email}</p>
                      </div>
                      <Badge variant={send.convertedAt ? "accent" : send.clickCount > 0 ? "accent" : "muted"}>
                        {send.convertedAt ? "Registrert" : send.clickCount > 0 ? "Klikket" : send.openCount > 0 ? "Åpnet" : send.status}
                      </Badge>
                    </div>
                    <p className="mt-2 truncate text-sm text-foreground">{send.subject}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Kølagt {formatDateTime(send.queuedAt)} · åpninger {send.openCount} · klikk {send.clickCount}
                    </p>
                    {send.lastClickedUrl ? (
                      <p className="mt-1 truncate text-xs text-muted-foreground">
                        Siste klikk: {send.lastClickedUrl}
                      </p>
                    ) : null}
                    {send.convertedAt ? (
                      <p className="mt-1 text-xs font-medium text-emerald-700">
                        Registrering {send.registrationOrgNumber ? `for org.nr ${send.registrationOrgNumber}` : "sporet"} {formatDateTime(send.convertedAt)}
                      </p>
                    ) : null}
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">
                  {isLoadingInsights ? "Laster aktivitet..." : "Ingen aktivitet registrert ennå."}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
